import React from "react";
import { ProductConsumer } from "../../Context/AddProWakel";
import { imageSrc } from "../../util/PublicOrderAPI";

const matchesSearch = (product, term) => {
    if (!term) return true;
    const needle = term.toLowerCase();
    return ["name", "code", "productCode"].some(
        (key) =>
            product[key] && String(product[key]).toLowerCase().includes(needle)
    );
};

// True when the search term is the product's code (or productCode) verbatim,
// not just a substring. Used to float an exact-code hit to the top of results.
const isExactCodeMatch = (product, term) => {
    const needle = (term || "").trim().toLowerCase();
    if (!needle) return false;
    return ["code", "productCode"].some(
        (key) => product[key] && String(product[key]).toLowerCase() === needle
    );
};

const matchesFilter = (product, filter) => {
    if (filter === "published") return !!product.isPublished;
    if (filter === "hidden") return !product.isPublished;
    return true;
};

const Thumb = ({ images, name, onOpen }) => {
    if (!Array.isArray(images) || images.length === 0) {
        return (
            <span className="rs-thumb rs-thumb--empty">
                <i className="fas fa-image"></i>
            </span>
        );
    }
    return (
        <button
            type="button"
            className="rs-thumb-btn"
            onClick={onOpen}
            title="بینینی رەسم"
        >
            <img className="rs-thumb" src={imageSrc(images[0])} alt={name || ""} />
            <span className="rs-thumb-btn__zoom">
                <i className="fas fa-search-plus"></i>
            </span>
            {images.length > 1 && (
                <span className="rs-thumb-btn__count rs-num">{images.length}</span>
            )}
        </button>
    );
};

const Lightbox = ({ images, name, index, onClose, onStep }) => {
    const many = images.length > 1;

    return (
        <div className="rs-lightbox" dir="rtl" onClick={onClose}>
            <button
                type="button"
                className="rs-lightbox__close"
                onClick={onClose}
                title="داخستن"
            >
                <i className="fas fa-times"></i>
            </button>

            <div className="rs-lightbox__stage" onClick={(e) => e.stopPropagation()}>
                <img
                    className="rs-lightbox__img"
                    src={imageSrc(images[index])}
                    alt={name || ""}
                />

                {many && (
                    <React.Fragment>
                        <button
                            type="button"
                            className="rs-lightbox__nav rs-lightbox__nav--prev"
                            onClick={() => onStep(-1)}
                            title="پێشوو"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                        <button
                            type="button"
                            className="rs-lightbox__nav rs-lightbox__nav--next"
                            onClick={() => onStep(1)}
                            title="دواتر"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                    </React.Fragment>
                )}

                <div className="rs-lightbox__caption">
                    <span className="rs-lightbox__name">{name || "—"}</span>
                    {many && (
                        <span className="rs-num">
                            {index + 1} / {images.length}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// Per-row category assignment (CATEGORY_API.md §4.5). Saving here fires a
// dedicated PATCH /setProductCategory — it is NOT part of the product save.
// Assigning locks the product (categoryLocked); the empty option clears+unlocks.
const CategoryCell = ({ product, options, onChange }) => {
    const current = product.category || null;
    const currentId = current ? current.id : "";
    const locked = !!product.categoryLocked && !!current;

    // Ensure the currently-assigned category still shows even if it's now
    // inactive (and therefore absent from the assignable options list).
    const opts = options.slice();
    if (current && !opts.some((c) => c.id === current.id)) {
        opts.unshift({ id: current.id, name: current.name });
    }

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <select
                className="rs-select"
                value={currentId}
                onChange={(e) => onChange(product._id, e.target.value)}
                title="گۆڕینی جۆری کاڵا"
            >
                <option value="">— بێ جۆر —</option>
                {opts.map((c) => (
                    <option key={c.id} value={c.id}>
                        {c.name}
                    </option>
                ))}
            </select>
            {current && (
                <span
                    className={`rs-badge ${locked ? "rs-badge--blue" : "rs-badge--grey"}`}
                    title={locked ? "دەستی دانراوە" : "خۆکار"}
                >
                    <i className={`fas ${locked ? "fa-thumbtack" : "fa-magic"}`}></i>
                    {locked ? "دەستی" : "خۆکار"}
                </span>
            )}
        </div>
    );
};

class ScroollTableStorefront extends React.Component {
    // The preview never leaves this screen, so it stays out of the global context.
    state = { preview: null };

    componentDidMount() {
        document.addEventListener("keydown", this.handleKey);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKey);
    }

    handleKey = (e) => {
        if (!this.state.preview) return;
        if (e.key === "Escape") this.closePreview();
        // The page reads right-to-left, so left moves forward.
        if (e.key === "ArrowLeft") this.step(1);
        if (e.key === "ArrowRight") this.step(-1);
    };

    openPreview = (product) => {
        const images = Array.isArray(product.images) ? product.images : [];
        if (images.length === 0) return;
        this.setState({
            preview: {
                images,
                name: product.nameofproduct || product.code || product.productCode,
                index: 0,
            },
        });
    };

    closePreview = () => this.setState({ preview: null });

    step = (delta) =>
        this.setState(({ preview }) => {
            if (!preview) return null;
            const total = preview.images.length;
            return {
                preview: {
                    ...preview,
                    index: (preview.index + delta + total) % total,
                },
            };
        });

    render() {
        const { filter } = this.props;
        const { preview } = this.state;

        return (
            <ProductConsumer>
                {(value) => {
                    const {
                        SF_products,
                        SF_search,
                        SF_togglePublish,
                        SF_OpenImageModal,
                        CT_categories,
                        CT_setProductCategory,
                        SF_paged,
                        SF_page,
                        SF_pages,
                        SF_total,
                        SF_pageNext,
                        SF_pagePrev,
                    } = value;

                    // Only active categories are assignable targets.
                    const assignable = (CT_categories || []).filter(
                        (c) => c.isActive !== false
                    );

                    // filter() returns a fresh array, so sorting it does not
                    // mutate SF_products. sort() is stable, so non-exact rows keep
                    // their original order while an exact-code hit rises to the top.
                    const list = SF_products
                        .filter(
                            (p) => matchesSearch(p, SF_search) && matchesFilter(p, filter)
                        )
                        .sort(
                            (a, b) =>
                                (isExactCodeMatch(b, SF_search) ? 1 : 0) -
                                (isExactCodeMatch(a, SF_search) ? 1 : 0)
                        );

                    return (
                        <div className="rs-card">
                            <div className="rs-table-wrap">
                                <table className="rs-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "70px" }}>رەسم</th>
                                            <th style={{ width: "140px" }}>کۆد</th>
                                            <th>ناو</th>
                                            <th style={{ width: "220px" }}>جۆر</th>
                                            <th className="is-center" style={{ width: "100px" }}>
                                                ژمارەی رەسم
                                            </th>
                                            <th className="is-center" style={{ width: "130px" }}>
                                                دۆخ
                                            </th>
                                            <th className="is-center" style={{ width: "240px" }}>
                                                کردار
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((product, i) => {
                                            const imageCount = Array.isArray(product.images)
                                                ? product.images.length
                                                : 0;
                                            const published = !!product.isPublished;
                                            return (
                                                <tr key={product._id || i}>
                                                    <td>
                                                        <Thumb
                                                            images={product.images}
                                                            name={product.nameofproduct}
                                                            onOpen={() => this.openPreview(product)}
                                                        />
                                                    </td>
                                                    <td className="rs-code rs-num">
                                                        {product.code || product.productCode || "—"}
                                                    </td>
                                                    <td className="rs-strong">{product.nameofproduct || "—"}</td>
                                                    <td>
                                                        <CategoryCell
                                                            product={product}
                                                            options={assignable}
                                                            onChange={CT_setProductCategory}
                                                        />
                                                    </td>
                                                    <td className="is-center rs-num">{imageCount}</td>
                                                    <td className="is-center">
                                                        <span
                                                            className={`rs-badge ${published ? "rs-badge--green" : "rs-badge--grey"
                                                                }`}
                                                        >
                                                            <i
                                                                className={`fas ${published ? "fa-globe" : "fa-eye-slash"
                                                                    }`}
                                                            ></i>
                                                            {published ? "بڵاوکراوە" : "شاراوە"}
                                                        </span>
                                                    </td>
                                                    <td className="is-center">
                                                        <button
                                                            className="rs-btn rs-btn--ghost rs-btn--sm"
                                                            onClick={() => SF_OpenImageModal(product)}
                                                            title="بەڕێوەبردنی رەسم"
                                                        >
                                                            <i className="fas fa-images"></i>
                                                            رەسم
                                                        </button>{" "}
                                                        <button
                                                            className={`rs-btn rs-btn--sm ${published ? "rs-btn--danger" : "rs-btn--green"
                                                                }`}
                                                            onClick={() => SF_togglePublish(product)}
                                                            title={published ? "شاردنەوە" : "بڵاوکردنەوە"}
                                                        >
                                                            <i
                                                                className={`fas ${published ? "fa-eye-slash" : "fa-globe"
                                                                    }`}
                                                            ></i>
                                                            {published ? "شاردنەوە" : "بڵاوکردنەوە"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {list.length === 0 && (
                                <div className="rs-empty">
                                    <i className="fas fa-inbox"></i>
                                    <p>هیچ کاڵایەک نەدۆزرایەوە</p>
                                </div>
                            )}

                            {/* Pager — only in paged (idle) mode. Searching/filtering
                                loads the full set client-side, so no pager there. */}
                            {SF_paged && (
                                <div
                                    style={{
                                        display: "flex",
                                        padding: "10px 14px",
                                        borderTop: "1px solid var(--rs-line)",
                                    }}
                                >
                                    <div className="rs-pager">
                                        <span className="rs-pager__count">
                                            کۆی کاڵاکان: <span className="rs-num">{SF_total}</span>
                                        </span>
                                        <button
                                            className="rs-btn rs-btn--ghost rs-btn--icon"
                                            onClick={SF_pagePrev}
                                            disabled={SF_page <= 1}
                                            title="پێشوو"
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                        <span className="rs-pager__pos rs-num">
                                            {SF_page} / {SF_pages}
                                        </span>
                                        <button
                                            className="rs-btn rs-btn--ghost rs-btn--icon"
                                            onClick={SF_pageNext}
                                            disabled={SF_page >= SF_pages}
                                            title="دواتر"
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {preview && (
                                <Lightbox
                                    images={preview.images}
                                    name={preview.name}
                                    index={preview.index}
                                    onClose={this.closePreview}
                                    onStep={this.step}
                                />
                            )}
                        </div>
                    );
                }}
            </ProductConsumer>
        );
    }
}

export default ScroollTableStorefront;
